import { identify, reset, track } from '@app/analytics/client';
import { authClient } from '@app/auth/client';
import * as Sentry from '@sentry/react';
import { Button } from '@app/ui';
import { useQuery, useSuspenseQuery } from '@tanstack/react-query';
import { createFileRoute, Link, Outlet, redirect, useRouter } from '@tanstack/react-router';
import { useEffect } from 'react';
import { BrandLink } from '../components/BrandLink';
import { NotificationBell } from '../components/notification-bell';
import { RouteErrorComponent } from '../components/route-error';
import { api } from '../lib/api';
import { getServerSession } from '../lib/auth-server';

export const Route = createFileRoute('/_authenticated')({
  beforeLoad: async ({ context, location }) => {
    const session = await getServerSession();
    if (!session) {
      // Preserve the URL the user was trying to reach so flows like
      // "click Start Pro on /pricing → sign in → land on Stripe Checkout"
      // survive the auth round-trip. Without this every protected destination
      // collapses back to /dashboard after sign-in.
      const returnUrl = `${location.pathname}${location.searchStr ?? ''}`;
      throw redirect({ to: '/auth/login', search: { returnUrl } });
    }

    // US-031.3: first-login NCAA-ID capture. Skip the redirect when:
    //  - we're already on /onboarding (avoid loop), or
    //  - the athlete has already filled the field or explicitly skipped, or
    //  - the user is org staff (owner/admin/compliance of any org) — the
    //    NCAA gate is athlete-only.
    // ensureQueryData warms the cache so dashboard/profile/banner all read
    // from the same cache entry on first paint.
    if (!location.pathname.startsWith('/onboarding')) {
      const { getServerApi } = await import('../lib/api-server');
      const serverApi = await getServerApi();
      const user = await context.queryClient.ensureQueryData(serverApi.user.me.queryOptions());
      // The NCAA capture gate is athlete-only. Staff and agents both opt
      // out of it at onboarding; without excluding 'agent' here an agent
      // would be bounced to /onboarding on every navigation.
      const isAthlete = user.userType !== 'staff' && user.userType !== 'agent';
      if (
        isAthlete &&
        !user.isOrgStaff &&
        !user.ncaaId &&
        !user.onboardingNcaaIdSkippedAt
      ) {
        const returnUrl = `${location.pathname}${location.searchStr ?? ''}`;
        throw redirect({ to: '/onboarding', search: { returnUrl } });
      }
    }

    return { session };
  },
  component: AuthenticatedLayout,
  errorComponent: ({ error }) => <RouteErrorComponent error={error} />,
});

function AuthenticatedLayout() {
  const router = useRouter();
  const { session } = Route.useRouteContext();

  // Fetch subscription so the identify() call below can set `plan` as a
  // person property. useQuery (not useSuspense) — we identify with plan:null
  // first and re-identify when the data lands. PostHog merges person
  // properties on subsequent identify calls.
  const { data: subscription } = useQuery(api.subscription.current.queryOptions());
  // user.me is already prefetched by beforeLoad — useSuspense reads from
  // the warm cache so this doesn't add a network round-trip. Drives the
  // conditional "Compliance" link in the navbar.
  const { data: me } = useSuspenseQuery(api.user.me.queryOptions());
  const isAgent = me.userType === 'agent';
  // Agents get the Agency link instead of Compliance — an agent who is
  // also a compliance member of some org is vanishingly rare, and the
  // agency surface is what they signed up for.
  const showComplianceLink = !isAgent && (me.isOrgStaff || me.userType === 'staff');
  // Agents keep Payors (they need payor records to create deals on an
  // athlete's behalf) but lose the athlete-only Deals/Payments views,
  // whose agent equivalents live under /agency.
  const showAthleteLinks = !isAgent;
  const currentPlan = subscription?.plan ?? null;

  // Identify the user for PostHog person profiles. With `person_profiles:
  // 'identified_only'` (see __root.tsx), no profile is created until this
  // fires — so before this hook, all events are anonymous.
  useEffect(() => {
    if (!session?.user) return;
    const user = session.user as {
      id: string;
      email: string;
      name?: string | null;
      role?: string | null;
      userType?: string | null;
      createdAt?: string | Date | null;
    };
    identify(user.id, {
      email: user.email,
      name: user.name ?? null,
      role: user.role ?? null,
      user_type: user.userType ?? null,
      created_at: user.createdAt
        ? new Date(user.createdAt).toISOString()
        : new Date().toISOString(),
      plan: currentPlan,
    });

    // If we set a signin_method marker on the login page, this is the user
    // returning from a fresh authentication round-trip. Fire signin_completed
    // once, then clear the marker so future visits don't double-fire.
    //
    // Sentry breadcrumb captures the marker state on EVERY mount of this
    // layout — so when signin_completed underfires (PostHog 30d shows 22/29
    // = 24% loss) we can finally distinguish "user never set marker" from
    // "marker cleared by an earlier mount" from "page reload lost
    // sessionStorage" in the trace timeline.
    const method = window.sessionStorage.getItem('signin_method') as 'magic_link' | 'google' | null;
    Sentry.addBreadcrumb({
      category: 'auth',
      message: '_authenticated mount: signin_method marker check',
      level: 'info',
      data: {
        marker: method ?? 'absent',
        user_id_set: Boolean(user?.id),
        has_pathname: typeof window !== 'undefined' ? window.location.pathname : null,
      },
    });
    if (method) {
      window.sessionStorage.removeItem('signin_method');
      // Heuristic: user accounts created in the last 60 seconds are "new".
      // Better-Auth's signUp event would be more accurate but isn't exposed
      // on the client; this is good enough for funnel analytics.
      const createdAt = user.createdAt ? new Date(user.createdAt).getTime() : 0;
      const isNewUser = createdAt > Date.now() - 60_000;
      track('signin_completed', { method, is_new_user: isNewUser });
    }
  }, [session, currentPlan]);

  const handleSignOut = async () => {
    // Don't capture 'signed_out' here — if authClient.signOut() rejects the
    // user stays authenticated but a sign-out event would have been logged
    // (false positive in funnels). The capture lives in PostHogIdentify's
    // authed -> unauthed transition instead, which only fires when the
    // session actually clears.
    await authClient.signOut();
    // Clear PostHog identity so a different user on a shared device
    // doesn't inherit this session's distinct_id.
    reset();
    router.navigate({ to: '/' });
  };

  return (
    <div className="min-h-screen bg-background">
      <header className="border-b bg-card">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 py-3">
          <div className="flex items-center justify-between gap-2">
            <BrandLink />
            <nav className="hidden sm:flex items-center gap-4">
              {/* Agents have no dashboard of their own — /dashboard
                  redirects them to the roster, so showing the link would
                  just be a second Agency button with a misleading name. */}
              {showAthleteLinks && (
                <Link
                  to="/dashboard"
                  className="text-sm text-muted-foreground hover:text-foreground transition-colors"
                  activeProps={{ className: 'text-foreground font-medium' }}
                  activeOptions={{ exact: true }}
                >
                  Dashboard
                </Link>
              )}
              {isAgent && (
                <Link
                  to="/agency/roster"
                  className="text-sm text-muted-foreground hover:text-foreground transition-colors"
                  activeProps={{ className: 'text-foreground font-medium' }}
                >
                  Agency
                </Link>
              )}
              {showAthleteLinks && (
                <Link
                  to="/deals"
                  className="text-sm text-muted-foreground hover:text-foreground transition-colors"
                  activeProps={{ className: 'text-foreground font-medium' }}
                >
                  Deals
                </Link>
              )}
              <Link
                to="/payors"
                search={{ page: 1 }}
                className="text-sm text-muted-foreground hover:text-foreground transition-colors"
                activeProps={{ className: 'text-foreground font-medium' }}
              >
                Payors
              </Link>
              {showAthleteLinks && (
                <Link
                  to="/payments"
                  className="text-sm text-muted-foreground hover:text-foreground transition-colors"
                  activeProps={{ className: 'text-foreground font-medium' }}
                >
                  Payments
                </Link>
              )}
              <Link
                to="/brands"
                className="text-sm text-muted-foreground hover:text-foreground transition-colors"
                activeProps={{ className: 'text-foreground font-medium' }}
              >
                Brands
              </Link>
              {showComplianceLink && (
                <Link
                  to="/compliance/submissions"
                  className="text-sm text-muted-foreground hover:text-foreground transition-colors"
                  activeProps={{ className: 'text-foreground font-medium' }}
                >
                  Compliance
                </Link>
              )}
            </nav>
            <div className="flex items-center gap-1 sm:gap-3 shrink-0">
              <NotificationBell />
              <Link
                to="/settings/organizations"
                className="text-sm text-muted-foreground hover:text-foreground transition-colors min-h-[44px] flex items-center"
                activeProps={{ className: 'text-foreground font-medium' }}
              >
                Orgs
              </Link>
              <Link
                to="/profile"
                className="text-sm text-muted-foreground hover:text-foreground transition-colors min-h-[44px] flex items-center"
                activeProps={{ className: 'text-foreground font-medium' }}
              >
                Profile
              </Link>
              <Button variant="ghost" size="sm" className="min-h-[44px]" onClick={handleSignOut}>
                Sign Out
              </Button>
            </div>
          </div>
          {/* Mobile nav */}
          <nav className="sm:hidden flex items-center gap-4 mt-2 pb-1 overflow-x-auto">
            {showAthleteLinks && (
              <Link
                to="/dashboard"
                className="text-sm text-muted-foreground hover:text-foreground transition-colors whitespace-nowrap"
                activeProps={{ className: 'text-foreground font-medium' }}
                activeOptions={{ exact: true }}
              >
                Dashboard
              </Link>
            )}
            {isAgent && (
              <Link
                to="/agency/roster"
                className="text-sm text-muted-foreground hover:text-foreground transition-colors whitespace-nowrap"
                activeProps={{ className: 'text-foreground font-medium' }}
              >
                Agency
              </Link>
            )}
            {showAthleteLinks && (
              <Link
                to="/deals"
                className="text-sm text-muted-foreground hover:text-foreground transition-colors whitespace-nowrap"
                activeProps={{ className: 'text-foreground font-medium' }}
              >
                Deals
              </Link>
            )}
            <Link
              to="/payors"
              search={{ page: 1 }}
              className="text-sm text-muted-foreground hover:text-foreground transition-colors whitespace-nowrap"
              activeProps={{ className: 'text-foreground font-medium' }}
            >
              Payors
            </Link>
            {showAthleteLinks && (
              <Link
                to="/payments"
                className="text-sm text-muted-foreground hover:text-foreground transition-colors whitespace-nowrap"
                activeProps={{ className: 'text-foreground font-medium' }}
              >
                Payments
              </Link>
            )}
            <Link
              to="/brands"
              className="text-sm text-muted-foreground hover:text-foreground transition-colors whitespace-nowrap"
              activeProps={{ className: 'text-foreground font-medium' }}
            >
              Brands
            </Link>
            {showComplianceLink && (
              <Link
                to="/compliance/submissions"
                className="text-sm text-muted-foreground hover:text-foreground transition-colors whitespace-nowrap"
                activeProps={{ className: 'text-foreground font-medium' }}
              >
                Compliance
              </Link>
            )}
          </nav>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-4 sm:px-6 py-6">
        <Outlet />
      </main>
    </div>
  );
}
